/**
 * Third-party tracking, loaded only after cookie consent.
 *
 * master.blade.php inlined Google Analytics, Google Tag Manager, two Facebook
 * Pixels and the Adform trackpoint, each wrapped in
 * `@if (!empty(Cookie::get('cookieConsent')))`. Because the page was rendered
 * per request, consent was evaluated before any script reached the browser.
 *
 * A single-page app has no such moment, so the tags are injected at runtime the
 * first time consent is present — on boot for a returning visitor, or the
 * instant the banner is accepted. Ids come from the environment instead of being
 * hardcoded, and nothing is requested while consent is absent.
 */

import { hasCookieConsent } from './cookies';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: ((...args: unknown[]) => void) & {
      callMethod?: (...args: unknown[]) => void;
      queue?: unknown[];
      push?: unknown;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: unknown;
    adf?: { ClickTrack: (el: unknown, id: number, label: string, opts: object) => void };
  }
}

const GA_ID = import.meta.env.VITE_GA_ID ?? '';
const GTM_ID = import.meta.env.VITE_GTM_ID ?? '';
const FB_PIXEL_IDS = (import.meta.env.VITE_FB_PIXEL_IDS ?? '')
  .split(',')
  .map((id) => id.trim())
  .filter(Boolean);
const ADFORM_ENABLED = import.meta.env.VITE_ADFORM_ENABLED === 'true';

/** Adform client id, previously hardcoded in entry.js. */
export const ADFORM_CLIENT_ID = 1446749;

let loaded = false;

function injectScript(src: string, attributes: Record<string, string> = {}): void {
  const element = document.createElement('script');
  element.async = true;
  element.src = src;
  for (const [key, value] of Object.entries(attributes)) {
    element.setAttribute(key, value);
  }
  document.head.appendChild(element);
}

function loadGoogleAnalytics(): void {
  if (!GA_ID) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID);
  injectScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`);
}

function loadTagManager(): void {
  if (!GTM_ID) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  injectScript(`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

function loadFacebookPixels(): void {
  if (FB_PIXEL_IDS.length === 0) {
    return;
  }

  if (!window.fbq) {
    const fbq = function stub(...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod(...args);
      } else {
        fbq.queue!.push(args);
      }
    } as NonNullable<Window['fbq']>;

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;

    window.fbq = fbq;
    window._fbq = fbq;
    injectScript('https://connect.facebook.net/en_US/fbevents.js');
  }

  for (const id of FB_PIXEL_IDS) {
    window.fbq('init', id);
  }
  window.fbq('track', 'PageView');
}

function loadAdform(): void {
  if (!ADFORM_ENABLED) {
    return;
  }
  injectScript('https://track.adform.net/serving/scripts/trackpoint/');
}

/**
 * Loads every consented tag once. Safe to call repeatedly — subsequent calls
 * are ignored, since the scripts track page views themselves.
 */
export function initAnalytics(): void {
  if (loaded || !hasCookieConsent()) {
    return;
  }
  loaded = true;

  loadGoogleAnalytics();
  loadTagManager();
  loadFacebookPixels();
  loadAdform();
}

/** Reports a virtual page view after a client-side navigation. */
export function trackPageView(path: string): void {
  if (!loaded) {
    return;
  }
  if (GA_ID && window.gtag) {
    window.gtag('event', 'page_view', { page_path: path });
  }
  if (FB_PIXEL_IDS.length > 0 && window.fbq) {
    window.fbq('track', 'PageView');
  }
}

/** Click event, replacing the `[data-ga]` listeners from entry.js. */
export function trackEvent(label: string): void {
  if (!loaded || !GA_ID || !window.gtag) {
    return;
  }
  window.gtag('event', label, {
    eventCategory: label,
    eventAction: 'click',
    transport: 'beacon',
  });
}

/** Adform click tracking, replacing the `[data-adform-tracking]` listeners. */
export function trackAdformClick(element: EventTarget | null, label: string): void {
  if (!loaded || !window.adf || !element) {
    return;
  }
  window.adf.ClickTrack(element, ADFORM_CLIENT_ID, label, {});
}
